import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Shield, Clock, Smartphone, Users, Key, CheckCircle, Zap, Lock, UserCheck, Calendar, Building, PoundSterling } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
const AccessControl = () => {
  const features = [{
    icon: Key,
    title: "Smart Lock Integration",
    description: "Seamlessly connect with popular smart lock systems for automated access control"
  }, {
    icon: Clock,
    title: "Time-Based Access",
    description: "Automatically grant access only during booked session times"
  }, {
    icon: Smartphone,
    title: "Mobile Access Codes",
    description: "Customers receive unique access codes directly on their phones"
  }, {
    icon: UserCheck,
    title: "Self-Service Entry",
    description: "Reduce staff overhead with fully automated customer check-ins"
  }, {
    icon: Shield,
    title: "Secure & Reliable",
    description: "Enterprise-grade security with backup access methods"
  }, {
    icon: Calendar,
    title: "Booking Integration",
    description: "Access codes automatically sync with booking schedules"
  }];
  const benefits = ["Reduce staff overhead by 70%", "24/7 facility access without staff presence", "Enhanced security with audit trails", "Seamless customer experience", "Real-time access monitoring", "Flexible room-by-room control"];
  const steps = [{
    number: "1",
    title: "Customer Books",
    description: "Customer selects their preferred time slot and completes booking"
  }, {
    number: "2",
    title: "Code Generated",
    description: "Unique access code is automatically created for the specific time window"
  }, {
    number: "3",
    title: "Access Granted",
    description: "Customer uses their code to enter during their booked session"
  }, {
    number: "4",
    title: "Auto Cleanup",
    description: "Access expires automatically when session ends"
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-blue-100 p-4 rounded-full">
                <Lock className="h-12 w-12 text-blue-600" />
              </div>
            </div>
            <Badge className="mb-4 bg-blue-50 text-blue-700 hover:bg-blue-100">
              Access Control
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Secure, Smart
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Access Control
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Give your customers seamless access to your facilities while maintaining complete security 
              and control. Our smart access system integrates with your bookings to provide automated, 
              time-based entry codes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg">
                Schedule Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, automated access control that works seamlessly with your booking system
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                    {step.number}
                  </div>
                  {index < steps.length - 1 && <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gray-200 -translate-y-0.5" />}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for comprehensive access control
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-lg">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Hardware Cost Comparison */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Affordable Hardware Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get enterprise-grade access control without the enterprise price tag
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* WellSuites Card */}
            <Card className="border-2 border-blue-200 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center py-2">
                <span className="font-semibold">Recommended</span>
              </div>
              <CardContent className="p-8 pt-16">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <img src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" alt="WellSuites" className="h-8 w-auto" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Wellsuites Hardware</h3>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <PoundSterling className="h-8 w-8 text-blue-600" />
                    <span className="text-4xl font-bold text-blue-600">200-300</span>
                  </div>
                  <p className="text-gray-600">Per smart lock device</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">Easy installation & setup</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">Integrated with booking system</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">24/7 technical support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">No monthly hardware fees</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">Backup access methods</span>
                  </div>
                </div>

                <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  Get Started
                </Button>
              </CardContent>
            </Card>

            {/* Other Providers Card */}
            <Card className="border shadow-lg">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-8 bg-gray-300 rounded flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-medium">Others</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Traditional Providers</h3>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <PoundSterling className="h-8 w-8 text-gray-600" />
                    <span className="text-4xl font-bold text-gray-600">700-1500</span>
                  </div>
                  <p className="text-gray-600">Per smart lock device</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Complex installation process</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Requires separate booking integration</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Limited support hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Additional monthly fees</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Basic access methods only</span>
                  </div>
                </div>

                <Button variant="outline" className="w-full" disabled>
                  Expensive Alternative
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
              <h4 className="text-xl font-semibold text-green-800 mb-2">Save Up To 85%</h4>
              <p className="text-green-700">Wellsuites offers enterprise-grade access control at a fraction of the cost. No hidden fees, no complex contracts - just affordable, reliable hardware.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Transform Your Operations
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Reduce operational costs while improving customer experience with automated access control.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-900">{benefit}</span>
                  </div>)}
              </div>
            </div>
            
            <div className="relative">
              <Card className="bg-gradient-to-br from-slate-100 to-slate-200 border-0">
                <CardContent className="p-12">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <Building className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Smart Facility Management</h3>
                    <p className="text-gray-600">
                      Monitor access, track usage patterns, and maintain complete security logs 
                      all from your dashboard.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <CallToAction />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" alt="WellSuites" className="h-6 w-auto brightness-0 invert" />
                <button className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2">
                  <img src="/lovable-uploads/b3af523e-f22c-4fe2-9969-5c45dc7caf3a.png" alt="Apple" className="h-4 w-4" />
                  Get the app
                </button>
              </div>
              <p className="text-gray-400 max-w-md">
                Empowering wellness centers and gyms with intelligent automation 
                and management solutions.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Explore</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Find Suites</a></li>
                <li><a href="#" className="hover:text-white transition-colors">My Account</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact us</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2025 WellSuites. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default AccessControl;